import React, { useState, useEffect, useMemo } from 'react';
import { V } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { Card, Btn, SkeletonCard } from '../../components/ui';
import { SocialAPI } from '../../utils/sync';
import { SectionTitle, track } from '../../components/kit';
import { getXPBonus } from './xpUtils';
import { SocialErrorCard } from './shared';

// Weekly XP leagues — Duolingo-style cohorts of up to 30 lifters per tier.
// XP is computed locally with the same base rates as the Iron Score and
// reported to /api/league, which places you in a cohort and returns standings.
export const LEAGUE_TIERS=[
  {name:"Rust",color:"#b45309"},{name:"Iron",color:"#94a3b8"},{name:"Steel",color:"#60a5fa"},
  {name:"Titanium",color:"#a78bfa"},{name:"Diamond",color:"#22d3ee"},
];
const pad=(n)=>String(n).padStart(2,"0");
const ymd=(d)=>`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
export function localMonday(){const d=new Date();const dow=d.getDay();d.setDate(d.getDate()-(dow===0?6:dow-1));return ymd(d);}
export function calcWeekXP(s,weekStart){
  const inWeek=(date)=>date&&date>=weekStart;
  const workouts=(s.workouts||[]).filter(w=>inWeek(w.date)).length;
  const nutritionDays=new Set((s.nutrition||[]).filter(n=>inWeek(n.date)).map(n=>n.date)).size;
  const checkins=(s.checkins||[]).filter(c=>inWeek(c.date)).length;
  const photos=(s.photos||[]).filter(p=>inWeek(p.date)).length;
  const bonus=(getXPBonus().log||[]).filter(e=>inWeek(e.date)).reduce((a,e)=>a+(e.amount||0),0);
  return {xp:workouts*30+nutritionDays*8+checkins*5+photos*15+bonus,workouts,nutritionDays,checkins,photos,bonus};
}

export function SocialLeague({s,d}){
  const email=s.profile?.email;
  const name=s.profile?.firstName||email?.split("@")[0]||"Lifter";
  const week=localMonday();
  const mine=useMemo(()=>calcWeekXP(s,week),[s.workouts,s.nutrition,s.checkins,s.photos,week]);
  const [data,setData]=useState(()=>LS.get("ft-league-cache")||null);
  const [loading,setLoading]=useState(!data);
  const [err,setErr]=useState(false);
  const load=()=>{
    if(!email)return;
    setErr(false);
    SocialAPI.leagueSync(email,week,mine.xp,name).then(r=>{
      setLoading(false);
      if(r?.success){setData(r);LS.set("ft-league-cache",r);}
      else if(r?.unavailable){setData({unavailable:true});}
      else setErr(true);
    });
  };
  useEffect(load,[email,mine.xp]);
  const tier=LEAGUE_TIERS[data?.tier??0]||LEAGUE_TIERS[0];
  const members=data?.members||[];
  const promote=data?.promote||0, demote=data?.demote||0;
  const daysLeft=(()=>{const end=new Date(week+"T00:00:00");end.setDate(end.getDate()+7);return Math.max(0,Math.ceil((end-new Date())/86400000));})();
  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>Weekly League</div>
          <div style={{fontSize:11,color:V.text3}}>Cohorts of 30 · new week every Monday · {daysLeft}d left</div>
        </div>
        <button type="button" onClick={load} aria-label="Refresh league" style={{background:"none",border:`1px solid ${V.cardBorder}`,borderRadius:8,cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontSize:11,color:V.text3,padding:"4px 8px",fontFamily:V.font}}>{Icons.refresh({size:12,color:V.text3})}Refresh</button>
      </div>
      {err&&<SocialErrorCard label="Could not load the league" onRetry={load}/>}
      {data?.last_week&&(
        <Card style={{padding:12,border:`1px solid ${data.last_week.outcome==="promoted"?V.accent:data.last_week.outcome==="demoted"?V.danger:V.cardBorder}40`}}>
          <div style={{fontSize:12,fontWeight:700,color:V.text}}>
            {data.last_week.outcome==="promoted"?"Promoted!":data.last_week.outcome==="demoted"?"Demoted":"Held your tier"}
          </div>
          <div style={{fontSize:11,color:V.text3}}>Last week you finished #{data.last_week.rank} of {data.last_week.size} in {LEAGUE_TIERS[data.last_week.tier]?.name||"your"} league.</div>
        </Card>
      )}
      <Card style={{padding:14,background:`linear-gradient(135deg,${tier.color}14,transparent)`,border:`1px solid ${tier.color}40`}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:48,height:48,borderRadius:14,background:`${tier.color}22`,border:`1px solid ${tier.color}55`,display:"flex",alignItems:"center",justifyContent:"center"}}>{Icons.trophy({size:24,color:tier.color})}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,fontWeight:700,color:tier.color,letterSpacing:".08em"}}>{tier.name.toUpperCase()} LEAGUE</div>
            <div style={{fontSize:22,fontWeight:900,color:V.text,fontFamily:V.mono,lineHeight:1.1}}>{mine.xp.toLocaleString()} <span style={{fontSize:12,color:V.text3,fontWeight:600}}>XP this week</span></div>
            {data?.you&&<div style={{fontSize:11,color:V.text3}}>Rank #{data.you.rank} of {members.length} · top {promote} promote{demote>0?` · bottom ${demote} demote`:""}</div>}
          </div>
        </div>
        <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}>
          {[["workouts",mine.workouts,30],["nutrition days",mine.nutritionDays,8],["check-ins",mine.checkins,5],["photos",mine.photos,15]].map(([l,v,r])=>(
            <span key={l} style={{fontSize:11,padding:"3px 8px",borderRadius:6,background:track(),color:V.text2}}>{v} {l} · {v*r} XP</span>
          ))}
          {mine.bonus>0&&<span style={{fontSize:11,padding:"3px 8px",borderRadius:6,background:track(),color:V.text2}}>bonus · {mine.bonus} XP</span>}
        </div>
      </Card>
      {loading&&<div><SkeletonCard lines={3}/></div>}
      {data?.unavailable&&<Card style={{padding:16,textAlign:"center"}}><div style={{fontSize:12,color:V.text3}}>Leagues are warming up — check back soon.</div></Card>}
      {members.length>0&&(
        <Card style={{padding:14}}>
          <SectionTitle icon={Icons.users} color={tier.color} style={{marginBottom:8}}>Your cohort</SectionTitle>
          {members.map((m,i)=>{
            const zone=i<promote?"promote":(demote>0&&i>=members.length-demote)?"demote":"stay";
            const zoneColor=zone==="promote"?V.accent:zone==="demote"?V.danger:V.text3;
            return(
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:i<members.length-1?`1px solid ${V.divider}`:"none",
                borderTop:zone==="demote"&&i===members.length-demote?`1px dashed ${V.danger}60`:undefined}}>
                <div style={{width:26,height:26,borderRadius:8,background:i<3?`${tier.color}22`:track(),display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:i<3?tier.color:V.text3,fontFamily:V.mono}}>{i+1}</div>
                <div style={{flex:1,minWidth:0,fontSize:13,fontWeight:m.you?800:600,color:m.you?V.accent:V.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name}{m.you?" (You)":""}</div>
                <div style={{fontSize:13,fontWeight:800,color:V.text,fontFamily:V.mono}}>{(m.xp||0).toLocaleString()}</div>
                <div style={{width:6,height:6,borderRadius:3,background:zoneColor,opacity:zone==="stay"?0.3:1}} aria-label={zone}/>
              </div>
            );
          })}
          <div style={{display:"flex",gap:12,marginTop:8,fontSize:11,color:V.text3}}>
            <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:6,height:6,borderRadius:3,background:V.accent}}/>Promotion zone</span>
            {demote>0&&<span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:6,height:6,borderRadius:3,background:V.danger}}/>Demotion zone</span>}
          </div>
        </Card>
      )}
      {!loading&&!data?.unavailable&&members.length<=1&&!err&&(
        <Card style={{padding:16,textAlign:"center"}}>
          <div style={{fontSize:12,fontWeight:700,color:V.text}}>You're first into this cohort</div>
          <div style={{fontSize:11,color:V.text3,marginTop:4}}>Up to 29 more lifters at your tier join as they log this week. Keep stacking XP.</div>
          <Btn full onClick={()=>d({type:"TAB",tab:"log_workout"})} s={{marginTop:10}}>Log a workout (+30 XP)</Btn>
        </Card>
      )}
    </div>
  );
}
